import { getApiUrl } from "../config/api";
import { TokenResponse } from "../types";

class TokenService {
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value: string) => void;
    reject: (error: any) => void;
  }> = [];

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token!);
      }
    });
    this.failedQueue = [];
  }

  getAccessToken(): string | null {
    return localStorage.getItem("token");
  }

  getRefreshToken(): string | null {
    return localStorage.getItem("refresh_token");
  }

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem("token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
  }

  async refreshTokens(): Promise<string> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const response = await fetch(getApiUrl("/api/auth/refresh"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Failed to refresh token");
      }

      const data: TokenResponse = await response.json();
      this.setTokens(data.access_token, data.refresh_token);

      this.processQueue(null, data.access_token);
      return data.access_token;
    } catch (error) {
      this.processQueue(error);
      this.clearTokens();
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  async getValidToken(): Promise<string> {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      throw new Error("No access token available");
    }

    // Проверяем, не истек ли токен
    try {
      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      const currentTime = Date.now() / 1000;

      if (payload.exp < currentTime) {
        // Токен истек, обновляем его
        return await this.refreshTokens();
      }

      return accessToken;
    } catch (error) {
      // Если не удалось декодировать токен, пробуем обновить
      return await this.refreshTokens();
    }
  }

  // Функция для создания запроса с автоматическим обновлением токена
  async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    try {
      const token = await this.getValidToken();

      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        // Токен недействителен, пробуем обновить
        const newToken = await this.refreshTokens();

        // Повторяем запрос с новым токеном
        return fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${newToken}`,
          },
        });
      }

      return response;
    } catch (error) {
      // Если не удалось обновить токен, очищаем хранилище
      this.clearTokens();
      throw error;
    }
  }
}

export const tokenService = new TokenService();
